/**
 * Message Template System for WhatsApp sharing
 * Provides pre-designed, engaging message templates for different sharing scenarios
 */

export interface RoomData {
  gameType: string;
  roomCode: string;
  playerName: string;
  maxPlayers: number;
  difficulty?: string;
}

export interface GameResult {
  gameType: string;
  players: Player[];
  winner: string | null;
  duration: number;
  finalState: any;
  achievements?: string[];
}

export interface Player {
  name: string;
  id: string;
  score?: number;
}

export interface Challenge {
  gameType: string;
  challengerName: string;
  difficulty?: string;
  message?: string;
  expiresAt: Date;
}

export interface TemplateCustomization {
  includeEmojis?: boolean;
  tone?: 'casual' | 'competitive' | 'friendly';
  language?: string;
  customMessage?: string;
}

export class MessageTemplates {
  private static instance: MessageTemplates;
  private readonly platformName = 'PlayHub Games AI';
  private readonly platformURL = window.location.origin;

  private constructor() {}

  public static getInstance(): MessageTemplates {
    if (!MessageTemplates.instance) {
      MessageTemplates.instance = new MessageTemplates();
    }
    return MessageTemplates.instance;
  }

  /**
   * Generate room invitation message
   */
  public generateRoomInvite(roomData: RoomData, customizations: TemplateCustomization = {}): string {
    const { includeEmojis = true, tone = 'friendly' } = customizations;
    
    const emojis = includeEmojis ? this.getGameEmojis(roomData.gameType) : { game: '', action: '', platform: '' };
    
    let message = '';
    
    switch (tone) {
      case 'competitive':
        message = `${emojis.action} Ready for a challenge? ${emojis.game}\n\n`;
        message += `${roomData.playerName} is waiting for you in a ${roomData.gameType} match!\n`;
        break;
      
      case 'casual':
        message = `${emojis.game} Hey! Want to play ${roomData.gameType}?\n\n`;
        message += `${roomData.playerName} started a game and there's room for ${roomData.maxPlayers - 1} more player${roomData.maxPlayers > 2 ? 's' : ''}.\n`;
        break;
      
      default: // friendly
        message = `${emojis.game} Join my ${roomData.gameType} game! ${emojis.action}\n\n`;
        message += `${roomData.playerName} is waiting for you to join.\n`;
    }

    message += `\n🎯 Room Code: *${roomData.roomCode}*\n`;
    
    if (roomData.difficulty) {
      message += `⚡ Difficulty: ${roomData.difficulty}\n`;
    }
    
    message += `👥 Players: 1/${roomData.maxPlayers}\n\n`;
    message += `Click to join instantly:`;
    // URL will be appended here by WhatsAppService, then we add powered by after

    return this.applyCustomMessage(message, customizations.customMessage);
  }

  /**
   * Generate game result sharing message
   */
  public generateGameResult(resultData: GameResult, customizations: TemplateCustomization = {}): string {
    const { includeEmojis = true, tone = 'friendly' } = customizations;
    
    const emojis = includeEmojis ? this.getGameEmojis(resultData.gameType) : { game: '', action: '', platform: '' };
    const isWin = resultData.winner !== null;
    const isDraw = resultData.winner === null && resultData.players.length > 1;
    
    let message = '';
    
    if (isWin) {
      const winner = resultData.players.find(p => p.name === resultData.winner);
      const winnerEmoji = includeEmojis ? '🏆' : '';
      
      switch (tone) {
        case 'competitive':
          message = `${winnerEmoji} VICTORY! ${emojis.game}\n\n`;
          message += `${winner?.name || resultData.winner} dominated in ${resultData.gameType}!\n`;
          break;
        
        case 'casual':
          message = `${emojis.game} Game finished! ${winnerEmoji}\n\n`;
          message += `${winner?.name || resultData.winner} won this round of ${resultData.gameType}.\n`;
          break;
        
        default: // friendly
          message = `${winnerEmoji} Great game of ${resultData.gameType}! ${emojis.game}\n\n`;
          message += `Congratulations to ${winner?.name || resultData.winner} for the win!\n`;
      }
    } else if (isDraw) {
      const drawEmoji = includeEmojis ? '🤝' : '';
      message = `${drawEmoji} Epic ${resultData.gameType} battle! ${emojis.game}\n\n`;
      message += `It's a draw! Both players played amazingly.\n`;
    } else {
      message = `${emojis.game} ${resultData.gameType} completed!\n\n`;
    }

    // Add game details
    message += `\n📊 Game Summary:\n`;
    resultData.players.forEach((player, index) => {
      const position = isWin && player.name === resultData.winner ? '🥇' : 
                     isDraw ? '🤝' : 
                     index === 0 ? '🥈' : '🥉';
      message += `${position} ${player.name}`;
      if (player.score !== undefined) {
        message += ` - ${player.score} points`;
      }
      message += '\n';
    });

    const minutes = Math.floor(resultData.duration / 60);
    const seconds = resultData.duration % 60;
    message += `⏱️ Duration: ${minutes}:${seconds.toString().padStart(2, '0')}\n`;

    // Add achievements if any
    if (resultData.achievements && resultData.achievements.length > 0) {
      message += `\n🎖️ Achievements:\n`;
      resultData.achievements.forEach(achievement => {
        message += `• ${achievement}\n`;
      });
    }

    message += `\n${emojis.action} Want a rematch? Click here:\n`;
    message += `${this.platformURL}?action=challenge&game=${resultData.gameType}\n\n`;
    message += `${emojis.platform} Play more games at ${this.platformName}`;

    return this.applyCustomMessage(message, customizations.customMessage);
  }

  /**
   * Generate challenge message
   */
  public generateChallenge(challengeData: Challenge, customizations: TemplateCustomization = {}): string {
    const { includeEmojis = true, tone = 'competitive' } = customizations;
    
    const emojis = includeEmojis ? this.getGameEmojis(challengeData.gameType) : { game: '', action: '', platform: '' };
    
    let message = '';
    
    switch (tone) {
      case 'casual':
        message = `${emojis.game} Hey! Up for a game?\n\n`;
        message += `${challengeData.challengerName} wants to play ${challengeData.gameType} with you.\n`;
        break;
      
      case 'friendly':
        message = `${emojis.action} Game challenge! ${emojis.game}\n\n`;
        message += `${challengeData.challengerName} is challenging you to ${challengeData.gameType}!\n`;
        break;
      
      default: // competitive
        message = `⚔️ CHALLENGE ISSUED! ${emojis.game}\n\n`;
        message += `${challengeData.challengerName} thinks they can beat you at ${challengeData.gameType}.\n`;
        message += `Are you ready to prove them wrong? ${emojis.action}\n`;
    }

    if (challengeData.difficulty) {
      message += `\n⚡ Difficulty: ${challengeData.difficulty}\n`;
    }

    if (challengeData.message) {
      message += `\n💬 "${challengeData.message}"\n`;
    }

    const expiresIn = Math.ceil((challengeData.expiresAt.getTime() - Date.now()) / (1000 * 60 * 60));
    message += `\n⏰ Challenge expires in ${expiresIn} hours\n\n`;
    
    message += `${emojis.action} Accept the challenge:\n`;
    message += `${this.platformURL}?action=accept-challenge&game=${challengeData.gameType}\n\n`;
    message += `${emojis.platform} ${this.platformName} - Where champions are made!`;

    return this.applyCustomMessage(message, customizations.customMessage);
  }

  /**
   * Generate general platform invitation
   */
  public generateGeneralInvite(customizations: TemplateCustomization = {}): string {
    const { includeEmojis = true, tone = 'friendly' } = customizations;
    
    let message = '';
    
    if (includeEmojis) {
      switch (tone) {
        case 'competitive':
          message = `🎮 Ready to dominate? 🏆\n\n`;
          message += `Join me on ${this.platformName} - the ultimate gaming platform!\n`;
          message += `🎯 Multiple games to master\n`;
          message += `⚔️ Challenge friends and AI\n`;
          message += `📊 Track your victories\n`;
          break;
        
        case 'casual':
          message = `🎮 Hey! Found this cool gaming site 😊\n\n`;
          message += `${this.platformName} has some fun games we can play together.\n`;
          message += `🎯 Easy to start playing\n`;
          message += `👥 Multiplayer support\n`;
          message += `📱 Works on any device\n`;
          break;
        
        default: // friendly
          message = `🎮 Let's play some games! 🎉\n\n`;
          message += `I've been having a blast on ${this.platformName}!\n`;
          message += `🎯 Awesome games collection\n`;
          message += `🤖 Smart AI opponents\n`;
          message += `👥 Real-time multiplayer\n`;
          message += `📊 Achievement tracking\n`;
      }
    } else {
      message = `Let's play some games!\n\n`;
      message += `Check out ${this.platformName} - it's got great games we can play together.\n`;
      message += `Multiple games available\n`;
      message += `Play against AI or friends\n`;
      message += `Works on any device\n`;
    }

    message += `\n🚀 Start playing now:\n${this.platformURL}\n\n`;
    
    if (includeEmojis) {
      message += `🎮 See you in the game!`;
    } else {
      message += `See you in the game!`;
    }

    return this.applyCustomMessage(message, customizations.customMessage);
  }

  /**
   * Generate achievement sharing message
   */
  public generateAchievementShare(achievement: string, gameType: string, customizations: TemplateCustomization = {}): string {
    const { includeEmojis = true, tone = 'friendly' } = customizations;
    
    const emojis = includeEmojis ? this.getGameEmojis(gameType) : { game: '', action: '', platform: '' };
    const achievementEmoji = includeEmojis ? '🏆' : '';
    
    let message = '';
    
    switch (tone) {
      case 'competitive':
        message = `${achievementEmoji} ACHIEVEMENT UNLOCKED! ${emojis.game}\n\n`;
        message += `Just crushed this milestone in ${gameType}:\n`;
        message += `🎖️ "${achievement}"\n\n`;
        message += `Think you can do better? ${emojis.action}`;
        break;
      
      case 'casual':
        message = `${achievementEmoji} Got a new achievement! ${emojis.game}\n\n`;
        message += `"${achievement}" in ${gameType}\n\n`;
        message += `Pretty happy about this one! 😊`;
        break;
      
      default: // friendly
        message = `${achievementEmoji} Achievement unlocked! ${emojis.game}\n\n`;
        message += `Just earned "${achievement}" in ${gameType}!\n\n`;
        message += `Want to try for this one too? ${emojis.action}`;
    }

    message += `\n\n🎮 Play ${gameType}:\n${this.platformURL}?game=${gameType}\n\n`;
    message += `${emojis.platform} ${this.platformName}`;

    return this.applyCustomMessage(message, customizations.customMessage);
  }

  /**
   * Customize template with user's personal message
   */
  public customizeTemplate(template: string, customizations: TemplateCustomization): string {
    let customized = template;

    // Apply tone modifications
    if (customizations.tone) {
      // This could be expanded to modify language based on tone
    }

    // Apply emoji preferences
    if (customizations.includeEmojis === false) {
      customized = this.removeEmojis(customized);
    }

    return this.applyCustomMessage(customized, customizations.customMessage);
  }

  /**
   * Get game-specific emojis
   */
  private getGameEmojis(gameType: string): { game: string; action: string; platform: string } {
    const emojiMap: Record<string, { game: string; action: string; platform: string }> = {
      'tic-tac-toe': { game: '⭕', action: '🎯', platform: '🎮' },
      'chess': { game: '♟️', action: '🏰', platform: '🎮' },
      'checkers': { game: '🔴', action: '👑', platform: '🎮' },
      'default': { game: '🎮', action: '🚀', platform: '🎯' }
    };

    return emojiMap[gameType.toLowerCase()] || emojiMap.default;
  }

  /**
   * Apply custom message to template
   */
  private applyCustomMessage(template: string, customMessage?: string): string {
    if (!customMessage) {
      return template;
    }

    // Add custom message at the beginning
    return `${customMessage}\n\n${template}`;
  }

  /**
   * Remove emojis from text
   */
  private removeEmojis(text: string): string {
    // Remove emoji characters using surrogate pair ranges
    return text.replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]|[\u2600-\u27BF]|[\uFE00-\uFE0F]/g, '');
  }

  /**
   * Get localized template (future feature)
   */
  public getLocalizedTemplate(type: string, locale: string): string {
    // Placeholder for future localization support
    // For now, return English templates
    return '';
  }

  /**
   * Validate template length for WhatsApp
   */
  public validateTemplateLength(template: string): { valid: boolean; length: number; maxLength: number } {
    const maxLength = 4096; // WhatsApp message limit
    const length = template.length;
    
    return {
      valid: length <= maxLength,
      length,
      maxLength
    };
  }

  /**
   * Truncate template if too long
   */
  public truncateTemplate(template: string, maxLength: number = 4000): string {
    if (template.length <= maxLength) {
      return template;
    }

    const truncated = template.substring(0, maxLength - 3);
    const lastNewline = truncated.lastIndexOf('\n');
    
    // Try to truncate at a natural break point
    if (lastNewline > maxLength * 0.8) {
      return truncated.substring(0, lastNewline) + '...';
    }
    
    return truncated + '...';
  }
}

// Export singleton instance
export const messageTemplates = MessageTemplates.getInstance();